'use client';

import { useState } from 'react';

export function VimHelper() {
  const [activeCategory, setActiveCategory] = useState('basics');
  const [searchTerm, setSearchTerm] = useState('');

  const categories = {
    basics: {
      name: 'Grundlagen',
      icon: '📖',
      commands: [
        { cmd: 'vi datei.txt', desc: 'Datei öffnen oder erstellen', mode: 'Terminal' },
        { cmd: 'i', desc: 'Insert Mode (vor Cursor)', mode: 'Normal' },
        { cmd: 'a', desc: 'Insert Mode (nach Cursor)', mode: 'Normal' },
        { cmd: 'ESC', desc: 'Zurück zum Normal Mode', mode: 'Insert' },
        { cmd: ':w', desc: 'Datei speichern', mode: 'Normal' },
        { cmd: ':q', desc: 'Vim beenden', mode: 'Normal' },
        { cmd: ':wq', desc: 'Speichern und beenden', mode: 'Normal' },
        { cmd: ':q!', desc: 'Beenden ohne speichern (erzwingen)', mode: 'Normal' },
        { cmd: 'ZZ', desc: 'Speichern und beenden (Shortcut)', mode: 'Normal' },
      ],
    },
    navigation: {
      name: 'Navigation',
      icon: '🧭',
      commands: [
        { cmd: 'h j k l', desc: 'Links, runter, hoch, rechts (Pfeiltasten)', mode: 'Normal' },
        { cmd: 'w', desc: 'Zum Wortanfang vorwärts', mode: 'Normal' },
        { cmd: 'b', desc: 'Zum Wortanfang rückwärts', mode: 'Normal' },
        { cmd: 'e', desc: 'Zum Wortende vorwärts', mode: 'Normal' },
        { cmd: '0', desc: 'Zeilenanfang', mode: 'Normal' },
        { cmd: '$', desc: 'Zeilenende', mode: 'Normal' },
        { cmd: 'gg', desc: 'Dateianfang', mode: 'Normal' },
        { cmd: 'G', desc: 'Dateiende', mode: 'Normal' },
        { cmd: ':42', desc: 'Gehe zu Zeile 42', mode: 'Normal' },
        { cmd: 'Ctrl+f', desc: 'Seite vorwärts', mode: 'Normal' },
        { cmd: 'Ctrl+b', desc: 'Seite rückwärts', mode: 'Normal' },
        { cmd: '%', desc: 'Zu passender Klammer springen', mode: 'Normal' },
      ],
    },
    editing: {
      name: 'Bearbeiten',
      icon: '✏️',
      commands: [
        { cmd: 'x', desc: 'Zeichen löschen', mode: 'Normal' },
        { cmd: 'dd', desc: 'Zeile löschen', mode: 'Normal' },
        { cmd: 'dw', desc: 'Wort löschen', mode: 'Normal' },
        { cmd: 'd$', desc: 'Bis Zeilenende löschen', mode: 'Normal' },
        { cmd: 'yy', desc: 'Zeile kopieren (yank)', mode: 'Normal' },
        { cmd: 'yw', desc: 'Wort kopieren', mode: 'Normal' },
        { cmd: 'p', desc: 'Nach Cursor einfügen', mode: 'Normal' },
        { cmd: 'P', desc: 'Vor Cursor einfügen', mode: 'Normal' },
        { cmd: 'u', desc: 'Rückgängig (undo)', mode: 'Normal' },
        { cmd: 'Ctrl+r', desc: 'Wiederherstellen (redo)', mode: 'Normal' },
        { cmd: 'r', desc: 'Einzelnes Zeichen ersetzen', mode: 'Normal' },
        { cmd: 'cw', desc: 'Wort ändern', mode: 'Normal' },
        { cmd: 'cc', desc: 'Zeile ändern', mode: 'Normal' },
        { cmd: 'o', desc: 'Neue Zeile unter Cursor öffnen', mode: 'Normal' },
        { cmd: 'O', desc: 'Neue Zeile über Cursor öffnen', mode: 'Normal' },
      ],
    },
    search: {
      name: 'Suchen & Ersetzen',
      icon: '🔍',
      commands: [
        { cmd: '/text', desc: 'Vorwärts nach "text" suchen', mode: 'Normal' },
        { cmd: '?text', desc: 'Rückwärts nach "text" suchen', mode: 'Normal' },
        { cmd: 'n', desc: 'Nächstes Suchergebnis', mode: 'Normal' },
        { cmd: 'N', desc: 'Vorheriges Suchergebnis', mode: 'Normal' },
        { cmd: ':%s/alt/neu/g', desc: 'Alle "alt" durch "neu" ersetzen (global)', mode: 'Normal' },
        { cmd: ':%s/alt/neu/gc', desc: 'Mit Bestätigung ersetzen', mode: 'Normal' },
        { cmd: ':s/alt/neu/', desc: 'In aktueller Zeile ersetzen', mode: 'Normal' },
        { cmd: '*', desc: 'Wort unter Cursor suchen', mode: 'Normal' },
      ],
    },
    visual: {
      name: 'Visual Mode',
      icon: '👁️',
      commands: [
        { cmd: 'v', desc: 'Visual Mode (zeichenweise)', mode: 'Normal' },
        { cmd: 'V', desc: 'Visual Line Mode (zeilenweise)', mode: 'Normal' },
        { cmd: 'Ctrl+v', desc: 'Visual Block Mode (blockweise)', mode: 'Normal' },
        { cmd: 'd', desc: 'Auswahl löschen', mode: 'Visual' },
        { cmd: 'y', desc: 'Auswahl kopieren', mode: 'Visual' },
        { cmd: '>', desc: 'Auswahl einrücken', mode: 'Visual' },
        { cmd: '<', desc: 'Auswahl ausrücken', mode: 'Visual' },
      ],
    },
    advanced: {
      name: 'Erweitert',
      icon: '🚀',
      commands: [
        { cmd: '.', desc: 'Letzten Befehl wiederholen', mode: 'Normal' },
        { cmd: '~', desc: 'Groß-/Kleinschreibung ändern', mode: 'Normal' },
        { cmd: ':set number', desc: 'Zeilennummern anzeigen', mode: 'Normal' },
        { cmd: ':set nonumber', desc: 'Zeilennummern ausblenden', mode: 'Normal' },
        { cmd: ':syntax on', desc: 'Syntax-Highlighting aktivieren', mode: 'Normal' },
        { cmd: ':split datei', desc: 'Horizontal splitten', mode: 'Normal' },
        { cmd: ':vsplit datei', desc: 'Vertikal splitten', mode: 'Normal' },
        { cmd: 'Ctrl+w w', desc: 'Zwischen Fenstern wechseln', mode: 'Normal' },
        { cmd: ':e datei.txt', desc: 'Andere Datei öffnen', mode: 'Normal' },
        { cmd: ':!befehl', desc: 'Shell-Befehl ausführen', mode: 'Normal' },
      ],
    },
  };

  const filteredCommands = () => {
    if (!searchTerm) return categories[activeCategory].commands;

    const allCommands = Object.values(categories).flatMap(cat =>
      cat.commands.map(cmd => ({ ...cmd, category: cat.name }))
    );

    return allCommands.filter(cmd =>
      cmd.cmd.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cmd.desc.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const getModeColor = (mode) => {
    switch (mode) {
      case 'Normal': return 'bg-blue-100 text-blue-800';
      case 'Insert': return 'bg-green-100 text-green-800';
      case 'Visual': return 'bg-purple-100 text-purple-800';
      case 'Terminal': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-8">
      <h3 className="font-heading font-bold text-2xl mb-4">
        Vi/Vim Cheat Sheet
      </h3>
      <p className="text-gray-700 mb-6">
        Interaktive Hilfe für Vi/Vim Editor. Finde schnell die richtigen Befehle für deine Aufgabe.
      </p>

      {/* Search */}
      <div className="mb-6">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Befehl oder Beschreibung suchen..."
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
        />
      </div>

      {!searchTerm && (
        <>
          {/* Category Tabs */}
          <div className="flex flex-wrap gap-2 mb-6">
            {Object.entries(categories).map(([key, cat]) => (
              <button
                key={key}
                onClick={() => setActiveCategory(key)}
                className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                  activeCategory === key
                    ? 'bg-accent text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {cat.icon} {cat.name}
              </button>
            ))}
          </div>

          {/* Mode Legend */}
          <div className="flex flex-wrap gap-2 mb-6 text-sm">
            <span className="font-semibold">Modi:</span>
            <span className={`px-2 py-1 rounded ${getModeColor('Normal')}`}>Normal</span>
            <span className={`px-2 py-1 rounded ${getModeColor('Insert')}`}>Insert</span>
            <span className={`px-2 py-1 rounded ${getModeColor('Visual')}`}>Visual</span>
            <span className={`px-2 py-1 rounded ${getModeColor('Terminal')}`}>Terminal</span>
          </div>
        </>
      )}

      {/* Commands List */}
      <div className="space-y-2">
        {filteredCommands().map((cmd, index) => (
          <div
            key={index}
            className="bg-gray-50 hover:bg-gray-100 rounded-lg p-4 transition-colors"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <code className="bg-gray-900 text-green-400 px-3 py-1 rounded font-mono text-sm font-bold">
                    {cmd.cmd}
                  </code>
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${getModeColor(cmd.mode)}`}>
                    {cmd.mode}
                  </span>
                  {searchTerm && cmd.category && (
                    <span className="text-xs text-gray-500">
                      {cmd.category}
                    </span>
                  )}
                </div>
                <p className="text-gray-700 text-sm">
                  {cmd.desc}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredCommands().length === 0 && (
        <div className="text-center text-gray-500 py-8">
          Keine Befehle gefunden für "{searchTerm}"
        </div>
      )}

      {/* Quick Start Guide */}
      <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <h4 className="font-semibold text-lg mb-4 text-yellow-900">⚡ Schnellstart für Anfänger</h4>
        <ol className="space-y-2 text-sm text-yellow-800 list-decimal list-inside">
          <li><strong>Datei öffnen:</strong> <code className="bg-yellow-100 px-2 py-1 rounded">vi datei.txt</code></li>
          <li><strong>Text bearbeiten:</strong> Drücke <kbd className="bg-yellow-100 px-2 py-1 rounded">i</kbd> für Insert Mode</li>
          <li><strong>Zurück zu Normal Mode:</strong> Drücke <kbd className="bg-yellow-100 px-2 py-1 rounded">ESC</kbd></li>
          <li><strong>Speichern:</strong> Tippe <code className="bg-yellow-100 px-2 py-1 rounded">:w</code> und drücke Enter</li>
          <li><strong>Beenden:</strong> Tippe <code className="bg-yellow-100 px-2 py-1 rounded">:q</code> und drücke Enter</li>
          <li><strong>Speichern & Beenden:</strong> Tippe <code className="bg-yellow-100 px-2 py-1 rounded">:wq</code> und drücke Enter</li>
        </ol>
      </div>

      {/* Tips */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h4 className="font-semibold text-lg mb-4 text-blue-900">💡 Pro-Tipps</h4>
        <ul className="space-y-2 text-sm text-blue-800">
          <li className="flex items-start gap-2">
            <span className="text-accent mt-1">→</span>
            <span>
              <strong>Vim-Tutor:</strong> Führe <code className="bg-blue-100 px-2 py-1 rounded">vimtutor</code> im Terminal aus für ein interaktives Tutorial
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-accent mt-1">→</span>
            <span>
              <strong>Kombinationen:</strong> Befehle wie <code className="bg-blue-100 px-2 py-1 rounded">d3w</code> (lösche 3 Wörter) oder <code className="bg-blue-100 px-2 py-1 rounded">y2j</code> (kopiere 2 Zeilen runter) sind möglich
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-accent mt-1">→</span>
            <span>
              <strong>Notausgang:</strong> Wenn du feststeckst, drücke mehrmals <kbd className="bg-blue-100 px-2 py-1 rounded">ESC</kbd> und tippe <code className="bg-blue-100 px-2 py-1 rounded">:q!</code>
            </span>
          </li>
        </ul>
      </div>
    </div>
  );
}
